import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal } from "./motion-primitives";
import { SectionLabel } from "./About";
import { useLang, Rich } from "@/i18n/LanguageContext";

const EASE = [0.22, 1, 0.36, 1];

const Item = ({ i, q, a, open, onToggle }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: i * 0.07, ease: EASE }}
    className={`border-b border-ink/15 transition-colors duration-300 ${open ? "bg-white/40" : "hover:bg-white/30"}`}
  >
    <button
      type="button"
      onClick={onToggle}
      data-testid={`faq-trigger-${i}`}
      data-cursor
      aria-expanded={open}
      className="group flex w-full items-center gap-5 px-2 py-6 text-left md:gap-8 md:px-4"
    >
      <span className={`font-sans text-[11px] tracking-[0.2em] transition-colors duration-300 ${open ? "text-teal" : "text-[#98a3a1]"}`}>
        {`0${i + 1}`}
      </span>
      <h3 className={`flex-1 font-serif text-xl leading-tight transition-colors duration-300 md:text-[27px] ${open ? "text-teal" : "text-ink group-hover:text-teal"}`}>
        {q}
      </h3>
      <motion.span
        animate={{ rotate: open ? 135 : 0 }}
        transition={{ duration: 0.35, ease: EASE }}
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${open ? "border-teal text-teal" : "border-ink/25 text-ink group-hover:border-teal group-hover:text-teal"}`}
      >
        <Plus size={16} />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key="content"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.45, ease: EASE }}
          className="overflow-hidden"
        >
          <p className="max-w-[640px] pb-8 pl-9 pr-4 font-sans text-[14px] leading-[1.8] text-[#596360] md:pl-16">
            {a}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

export const Faq = () => {
  const { c } = useLang();
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="section-faq" className="bg-paper2 px-6 py-24 text-ink md:px-[8vw] md:py-32">
      <Reveal>
        <SectionLabel n="07">{c.faq.label}</SectionLabel>
      </Reveal>
      <div className="grid gap-10 md:grid-cols-[0.85fr_1.15fr] md:gap-[8vw]">
        <Reveal>
          <h2 className="font-serif text-[clamp(40px,4.6vw,68px)] font-normal leading-[1.0] tracking-[-0.02em] md:sticky md:top-28">
            <Rich text={c.faq.title} />
          </h2>
        </Reveal>
        <div data-testid="faq-accordion" className="border-t border-ink/15">
          {c.faq.items.map((f, i) => (
            <Item key={i} i={i} q={f.q} a={f.a} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </div>
      </div>
    </section>
  );
};
